import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { onboardingSettingsV2ToThemeV2 } from '@modules/themes/utils';
import { ThemeV2SelectionList, type ThemeV2SelectionSource } from './ThemeV2SelectionList';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_ICON_BUTTON_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from './fullscreenDialogChrome';

/** `add` / `add-and-apply`: the strategy host. `change`: the onboarding host (one action). */
export type StrategyThemeSelectionAction = 'add' | 'add-and-apply' | 'change';

/**
 * `strategy` (default): "Select Theme" with Add theme / Add & apply.
 * `onboarding`: "Change onboarding theme" with one Change theme action, the current theme marked and an
 * info alert that existing screens keep their look (onboarding theme switch, admin only).
 */
export type ThemeSelectionDialogMode = 'strategy' | 'onboarding';

export interface StrategyThemeSelectionOption {
  id: string;
  name: string;
  source: 'project' | 'global';
  isActive?: boolean;
  settings: OnboardingThemeSettingsV2;
}

export interface StrategyThemeSelectionDialogProps {
  open: boolean;
  mode?: ThemeSelectionDialogMode;
  title?: string;
  options: StrategyThemeSelectionOption[];
  selectedOption: StrategyThemeSelectionOption | null;
  projectId?: string;
  loading?: boolean;
  error?: string | null;
  /** `${source}:${id}` of the theme the host already uses: marked "Current"; choosing it changes nothing. */
  currentOptionKey?: string | null;
  onClose: () => void;
  onSelect: (option: StrategyThemeSelectionOption) => void;
  onConfirm: (action: StrategyThemeSelectionAction) => void;
}

export const getThemeSelectionOptionKey = (option: StrategyThemeSelectionOption) => `${option.source}:${option.id}`;
const getOptionKey = getThemeSelectionOptionKey;

/**
 * The option an onboarding already uses, from its recorded source (`onboardings/{o}.sourceThemeId` +
 * `sourceThemeKind`). Older onboardings have no kind: the id is matched across both lists, product
 * themes first. No recorded source (or a theme no longer listed) → no "Current" option.
 */
export function resolveCurrentThemeOptionKey(
  options: StrategyThemeSelectionOption[],
  sourceThemeId?: string | null,
  sourceThemeKind?: 'project' | 'system' | null,
): string | null {
  if (!sourceThemeId) return null;
  const wantedSource = sourceThemeKind === 'system' ? 'global' : sourceThemeKind === 'project' ? 'project' : null;
  const match = wantedSource
    ? options.find((option) => option.source === wantedSource && option.id === sourceThemeId)
    : options.find((option) => option.source === 'project' && option.id === sourceThemeId)
      ?? options.find((option) => option.source === 'global' && option.id === sourceThemeId);
  return match ? getOptionKey(match) : null;
}

const MODE_COPY = {
  strategy: {
    title: 'Select Theme',
    testIdPrefix: 'strategy-theme-selection',
    loadingTitle: 'Preparing theme apply...',
    loadingDetail: 'Keep this dialog open until the preview is ready.',
  },
  onboarding: {
    title: 'Change onboarding theme',
    testIdPrefix: 'onboarding-theme-selection',
    loadingTitle: 'Changing theme…',
    loadingDetail: 'Saving the theme and screen templates.',
  },
} as const;

export const StrategyThemeSelectionDialog: React.FC<StrategyThemeSelectionDialogProps> = ({
  open,
  mode = 'strategy',
  title,
  options,
  selectedOption,
  projectId,
  loading = false,
  error,
  currentOptionKey = null,
  onClose,
  onSelect,
  onConfirm,
}) => {
  const { sources, optionByThemeId } = useMemo(() => {
    const mapped = options.map(option => {
      const id = getOptionKey(option);
      return {
        option,
        theme: onboardingSettingsV2ToThemeV2(option.settings, {
          id,
          name: option.name,
        }),
      };
    });

    const map = new Map(mapped.map(({ theme, option }) => [theme.id, option]));
    const projectThemes = mapped
      .filter(({ option }) => option.source === 'project')
      .map(({ theme }) => theme);
    const globalThemes = mapped
      .filter(({ option }) => option.source === 'global')
      .map(({ theme }) => theme);

    const nextSources: ThemeV2SelectionSource[] = [];
    if (projectThemes.length > 0) {
      nextSources.push({
        id: 'project',
        label: 'Product themes',
        themes: projectThemes,
        themeSource: 'project',
        chipLabel: 'Product',
        chipColor: 'primary',
      });
    }
    nextSources.push({
      id: 'system',
      label: 'System themes',
      themes: globalThemes,
      themeSource: 'global',
      chipLabel: 'Global',
    });

    return { sources: nextSources, optionByThemeId: map };
  }, [options]);

  const copy = MODE_COPY[mode];
  const testId = (suffix: string) => `${copy.testIdPrefix}-${suffix}`;
  const selectedThemeId = selectedOption ? getOptionKey(selectedOption) : null;
  const confirmDisabled = loading || !selectedOption;
  const changeDisabled = confirmDisabled || (currentOptionKey !== null && selectedThemeId === currentOptionKey);

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={loading ? undefined : onClose}
      aria-labelledby={testId('title')}
      data-testid={testId('dialog')}
      sx={FULLSCREEN_DIALOG_PAPER_SX}
    >
      <Box sx={FULLSCREEN_DIALOG_HEADER_SX}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          data-testid={testId('close')}
          sx={FULLSCREEN_DIALOG_ICON_BUTTON_SX}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography id={testId('title')} variant="h6" sx={FULLSCREEN_DIALOG_TITLE_SX}>
          {title ?? copy.title}
        </Typography>
        {mode === 'onboarding' ? (
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              onClick={() => onConfirm('change')}
              disabled={changeDisabled}
              data-testid={testId('change')}
              sx={FULLSCREEN_DIALOG_BUTTON_SX}
            >
              {loading && options.length > 0 ? <CircularProgress color="inherit" size={16} /> : 'Change theme'}
            </Button>
          </Stack>
        ) : (
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            onClick={() => onConfirm('add')}
            disabled={confirmDisabled}
            data-testid="strategy-theme-selection-add"
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
          >
            {loading ? <CircularProgress color="inherit" size={16} /> : 'Add theme'}
          </Button>
          <Button
            variant="contained"
            onClick={() => onConfirm('add-and-apply')}
            disabled={confirmDisabled}
            data-testid="strategy-theme-selection-add-apply"
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
          >
            {loading ? <CircularProgress color="inherit" size={16} /> : 'Add & apply'}
          </Button>
        </Stack>
        )}
      </Box>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden', px: 3, py: 2 }}>
        {loading && options.length > 0 && (
          <Box
            data-testid={testId('loading-overlay')}
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Theme-aware scrim: the copy below must stay readable in light AND dark mode.
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
              backdropFilter: 'blur(2px)',
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.primary">
                {copy.loadingTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {copy.loadingDetail}
              </Typography>
            </Stack>
          </Box>
        )}
        <Stack spacing={2} sx={{ height: '100%' }}>
          {error && <Alert severity="error" data-testid={testId('error')}>{error}</Alert>}
          {mode === 'onboarding' && (
            <Alert severity="info" variant="outlined" role="status" data-testid={testId('info')} sx={{ maxWidth: 880 }}>
              Replaces this onboarding&apos;s theme and screen templates; new screens use it. Existing screens keep
              their look: restyle them later with <b>Bulk actions → Update UI from theme</b>. Theme edits made
              inside this onboarding are replaced.
            </Alert>
          )}
          {/* flex: the list scrolls under the alerts instead of being pushed past the bottom edge */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ThemeV2SelectionList
              sources={sources}
              selectedThemeId={selectedThemeId}
              currentThemeId={currentOptionKey}
              projectId={projectId}
              loading={loading && options.length === 0}
              emptyMessage="No themes available."
              cardTestIdPrefix={testId('card-')}
              onSelect={(theme) => {
                if (loading) return;
                const option = optionByThemeId.get(theme.id);
                if (option) onSelect(option);
              }}
            />
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default StrategyThemeSelectionDialog;
